import { Canvas } from '@react-three/fiber';
import {
  OrbitControls,
  GizmoHelper,
  GizmoViewcube,
  Stage,
  Environment,
  PerspectiveCamera,
  Stars,
} from '@react-three/drei';
import * as THREE from 'three';
import { useColor } from '@/contexts/ColorContext';
import { ThumbnailCapture } from './ThumbnailCapture';

export function ThreeScene({ geometry }: { geometry: THREE.BufferGeometry }) {
  const { color } = useColor();

  return (
    <div className="relative h-full w-full overflow-hidden">
      <Canvas className="block h-full w-full" gl={{ preserveDrawingBuffer: true }}>
        {/* Thumbnail capture - must be inside Canvas to access WebGL context */}
        <ThumbnailCapture geometry={geometry} />

        {/* Deep space background */}
        <color attach="background" args={['#050510']} />

        {/* Bright stars - far back to avoid model overlap */}
        <Stars
          radius={500}
          depth={100}
          count={5000}
          factor={12}
          saturation={1}
          fade={false}
          speed={0}
        />

        {/* Secondary star layer for depth */}
        <Stars
          radius={600}
          depth={150}
          count={3000}
          factor={8}
          saturation={0.9}
          fade={false}
          speed={0}
        />

        {/* Perspective camera only */}
        <PerspectiveCamera
          makeDefault
          position={[-100, 100, 100]}
          fov={45}
          near={0.1}
          far={1000}
          zoom={0.4}
        />

        <Stage environment={null} intensity={0.6} position={[0, 0, 0]}>
          <Environment files={`${import.meta.env.BASE_URL}/city.hdr`} />
          <ambientLight intensity={0.8} />
          <directionalLight position={[5, 5, 5]} intensity={1.2} castShadow />
          <directionalLight position={[-5, 5, 5]} intensity={0.2} />
          <directionalLight position={[-5, 5, -5]} intensity={0.2} />
          <directionalLight position={[0, 5, 0]} intensity={0.2} />
          <directionalLight position={[-5, -5, -5]} intensity={0.6} />
          <mesh
            geometry={geometry}
            rotation={[-Math.PI / 2, 0, 0]}
            position={[0, 0, 0]}
          >
            <meshStandardMaterial
              color={color}
              metalness={0.6}
              roughness={0.3}
              envMapIntensity={0.3}
            />
          </mesh>
        </Stage>
        <OrbitControls makeDefault enableDamping={true} dampingFactor={0.05} />
        <GizmoHelper alignment="bottom-right" margin={[80, 90]}>
          <GizmoViewcube />
        </GizmoHelper>
      </Canvas>
    </div>
  );
}
